import React, {useEffect} from 'react';
import {graphql} from 'react-relay';
import {Button, EMPHASIS, FlexRow, Link, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {
	actionsFor,
	describeLastUsed,
	describeWhen,
	FlyPreviewRow,
	POLL_INTERVAL_MS,
	previewLabel,
	rowStatus,
	statusLabel,
	statusText,
	STOP_WATCH_MS,
} from './flyPreviewRows';

// Fetched into the Mission Control store; the FlyPreview record updates by id and the page's
// fragment re-renders this row, so the poll result never needs to be read here.
const flyPreviewTableRowPreviewQuery = graphql`
	query FlyPreviewTableRow_PreviewQuery($slug: String!) {
		viewer {
			missionControlData {
				id
				flyPreview(slug: $slug) {
					id
					slug
					branchName
					webUrl
					status
					machinesTotal
					machinesRunning
					lastUsedAt
					lastUsedKnown
					createdAt
					deployedAt
				}
			}
		}
	}
`;

const STATUS_PILL: Record<string, PillVariant> = {
	RUNNING: PillVariant.SUCCESS,
	STARTING: PillVariant.INFORMATION,
	STOPPING: PillVariant.INFORMATION,
	DEGRADED: PillVariant.HIGHLIGHT,
	STOPPED: PillVariant.NEUTRAL,
};

export interface FlyPreviewTableRowProps {
	row: FlyPreviewRow;
	busy: boolean;
	// A Stop was sent from this page and the row is waiting for fly to finish it.
	stopping: boolean;
	onStart: (row: FlyPreviewRow) => void;
	onStop: (row: FlyPreviewRow) => void;
	onStopSettled: (slug: string) => void;
}

export function FlyPreviewTableRow({
	row,
	busy,
	stopping,
	onStart,
	onStop,
	onStopSettled,
}: FlyPreviewTableRowProps): JSX.Element {
	const {fetch} = useForecastFetchQuery(flyPreviewTableRowPreviewQuery, false, true);
	const status = rowStatus(row, stopping);
	const polling = status === 'STARTING' || status === 'STOPPING';

	useEffect(() => {
		if (!polling) {
			return undefined;
		}
		const timer = window.setInterval(() => fetch({slug: row.slug}), POLL_INTERVAL_MS);
		return () => window.clearInterval(timer);
		// `fetch` is a new function every render and deliberately left out of the dependency list; the
		// poll depends only on the row. web-front does not enable react-hooks/exhaustive-deps.
	}, [polling, row.slug]);

	// The stop is settled once fly reports the stack stopped, or when the watch runs out and the row
	// goes back to showing whatever the server says (DEGRADED, if a machine refused to stop).
	useEffect(() => {
		if (!stopping) {
			return undefined;
		}
		if (row.status === 'STOPPED') {
			onStopSettled(row.slug);
			return undefined;
		}
		const timer = window.setTimeout(() => onStopSettled(row.slug), STOP_WATCH_MS);
		return () => window.clearTimeout(timer);
	}, [stopping, row.status === 'STOPPED', row.slug]);

	const actions = actionsFor(status);
	return (
		<tr>
			<td>
				<Text>{previewLabel(row)}</Text>
			</td>
			<td>
				<Pill variant={STATUS_PILL[status] ?? PillVariant.NEUTRAL}>{statusLabel(row, status)}</Pill>
			</td>
			<td>
				<Text noWrap>{describeLastUsed(row, Date.now())}</Text>
			</td>
			<td>
				<Text noWrap>{describeWhen(row.createdAt, Date.now())}</Text>
			</td>
			<td>
				<Text noWrap>{describeWhen(row.deployedAt, Date.now())}</Text>
			</td>
			<td>
				{status === 'RUNNING' || status === 'DEGRADED' ? (
					<Link href={row.webUrl} target="_blank" rel="noopener noreferrer">
						Open
					</Link>
				) : (
					<Text noWrap>—</Text>
				)}
			</td>
			<td>
				<FlexRow gap="xs" alignItems="center">
					{actions.includes('start') && (
						<Button emphasis={EMPHASIS.MEDIUM} icon="play" disabled={busy} onPress={() => onStart(row)}>
							Start
						</Button>
					)}
					{actions.includes('stop') && (
						<Button emphasis={EMPHASIS.MEDIUM} icon="stop" disabled={busy} onPress={() => onStop(row)}>
							Stop
						</Button>
					)}
					{actions.includes('busy') && <Text noWrap>{statusText(status)}</Text>}
				</FlexRow>
			</td>
		</tr>
	);
}
